"use client";

import React from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { RequiredMarker } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { HelpTip } from "@/components/ui/help-tip";
import { cn } from "@/lib/utils";
import type { FieldConfig, GroupConfig } from "./types";
import { getFieldComponent } from "./fieldRegistry";
import { FormActionBar, type FormErrorSummaryItem } from "./FormActionBar";
import { useLingui } from "@lingui/react/macro";
import { useEscapeCancel } from "@/hooks/useEscapeCancel";
import { useDirtyGuard } from "@/hooks/useDirtyGuard";
import {
  applyServerFieldErrors,
  resolveFieldName,
  serverFieldToFormPath,
} from "@/lib/forms/applyServerFieldErrors";
import { errorToastText } from "@/lib/translateBackendError";
import type {
  Resolver,
  DefaultValues,
  ControllerRenderProps,
  FieldErrors,
  FieldPath,
  FieldValues,
  SubmitHandler,
} from "react-hook-form";

/**
 * Field types whose control is a `role="group"` container rather than a single
 * labelable element. `<label htmlFor>` cannot bind to these, so they supply their
 * own accessible name via `aria-label` and the rendered label is a caption only.
 */
const GROUP_FIELD_TYPES = new Set(["checklist", "tristate"]);

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Change detector for the `defaultValues` prop: consumers often rebuild the
 * object on every render, so identity says nothing. Null when the values
 * cannot be serialised, which turns the post-save re-baseline off.
 */
function defaultsKeyOf(values: unknown): string | null {
  try {
    return JSON.stringify(values ?? null);
  } catch {
    return null;
  }
}

/**
 * FormSubmitContext gives a submit handler the two pieces of react-hook-form
 * state it cannot otherwise reach: which fields the user actually TOUCHED, and a
 * way to write a field back without resetting the rest of the form.
 *
 * It exists for issue #1224 / decision D11: a derived-status field must be
 * dropped from the payload unless the admin changed it in THIS form session, and
 * a 422 rejection must refresh only that field while every other dirty value the
 * admin typed stays exactly as typed.
 */
export interface FormSubmitContext<T extends Record<string, unknown>> {
  /** react-hook-form's dirty map: a field is present and true once it changed. */
  dirtyFields: Partial<Record<keyof T, unknown>>;
  /** Writes one field. Pass `shouldDirty: false` to refresh it without marking it edited. */
  setValue: (
    name: FieldPath<T>,
    value: unknown,
    options?: { shouldDirty?: boolean },
  ) => void;
  /**
   * Makes a field's current value its new pristine baseline, so Cancel/Escape
   * no longer revert it. For a handler that persists in steps (#1684,
   * Organisation + timezone): after one step succeeded, re-baseline its fields
   * so a later failing step leaves only its own fields dirty.
   */
  markSaved?: (names: Array<FieldPath<T>>) => void;
}

// `TInput` is the schema's INPUT type, which differs from its parsed output `T`
// whenever a field carries a Zod `.default()` (see ChildrenForm). It defaults to
// `T`, so every existing call site keeps inferring exactly one type.
interface BaseEditableFormProps<
  T extends Record<string, unknown>,
  TInput = T,
> {
  schema: z.ZodType<T, TInput>;
  fields: FieldConfig<T>[];
  groups: GroupConfig<T>[];
  defaultValues?: Partial<TInput>;
  mode: "create" | "edit";
  onSubmit: (values: T, ctx: FormSubmitContext<T>) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  onDelete?: () => void | Promise<void>;
  isDeleting?: boolean;
  deleteButtonLabel?: string;
  /** Override the primary submit-button label (defaults to Create/Save by mode). */
  submitLabel?: string;
  /**
   * Blocks submission for a domain reason the form itself cannot see (e.g. the record is
   * linked to an archived parent). In-flight submits are already blocked without this.
   */
  submitDisabled?: boolean;
  children?: React.ReactNode;
  /**
   * Extra content rendered INSIDE a group card, below that group's fields, keyed
   * by `GroupConfig.id`. Use it when the content belongs next to its fields (a
   * status badge, a file's actions) rather than below the whole form, which is
   * what `children` gives you.
   */
  groupSlots?: Record<string, React.ReactNode>;
  /**
   * Replaces the default `auto-fit-cards` track definition on the grid that lays
   * out the group cards. For a form whose cards are deliberately narrow and read
   * as one strip — the weekday cards of the expected times (#1583) — the 400px
   * minimum of the default grid is too wide.
   */
  groupsClassName?: string;
  /**
   * Suppress the default Cancel/Submit footer buttons. For multi-step flows that
   * render their own Back/Next/finish controls — pass those as `children`, which
   * render INSIDE the <form>, so a `type="submit"` child still goes through this
   * form's validation and submit handler. The error summary stays visible.
   */
  hideDefaultActions?: boolean;
  inSheet?: boolean;
  /**
   * Pins the action bar to the bottom edge of the content area (inside a dialog:
   * to the dialog's bottom edge). Defaults to true; ignored (false) when
   * `inSheet`. Pass `false` for a form rendered inline inside a Card in the
   * middle of a page.
   */
  pinnedFooter?: boolean;
  /**
   * Reports react-hook-form's `isDirty` outward on every change (issue #315).
   *
   * The form arms `beforeunload` itself. A host that passes this prop together
   * with `onCancel` owns the in-app guard: the form then calls `onCancel`
   * without asking, and the host routes it, and its other exits (a tab
   * switch, a back link), through its own `useDirtyGuard(isDirty).confirmLeave`.
   * Without this prop the form asks before `onCancel` and Escape itself.
   */
  onDirtyChange?: (dirty: boolean) => void;
  onFieldChange?: (
    name: string,
    value: unknown,
    setValue: (
      name: FieldPath<T>,
      value: unknown,
      options?: { shouldDirty?: boolean },
    ) => void,
  ) => void;
}

export function BaseEditableForm<
  T extends Record<string, unknown>,
  TInput = T,
>({
  schema,
  fields,
  groups,
  defaultValues,
  mode,
  onSubmit,
  onCancel,
  isSubmitting,
  onDelete,
  isDeleting,
  deleteButtonLabel,
  submitLabel,
  submitDisabled,
  children,
  groupSlots,
  groupsClassName,
  hideDefaultActions = false,
  inSheet = false,
  pinnedFooter = true,
  onDirtyChange,
  onFieldChange,
}: BaseEditableFormProps<T, TInput>) {
  const { t, i18n } = useLingui();
  const [isAvatarUploading, setIsAvatarUploading] = React.useState(false);
  const footerPinned = !inSheet && pinnedFooter;

  const methods = useForm<T>({
    resolver: zodResolver(
      schema as unknown as z.ZodType<FieldValues, FieldValues>,
    ) as unknown as Resolver<T>,
    defaultValues: defaultValues as DefaultValues<T>,
    mode: "onBlur",
    // `focusFirstError` moves focus in display order and waits for disabled
    // fields to re-enable; react-hook-form's own focus (on by default) would
    // jump first, in registration order.
    shouldFocusError: false,
  });

  const {
    control,
    handleSubmit,
    formState: {
      errors,
      isSubmitting: formStateIsSubmitting,
      dirtyFields,
      isDirty,
    },
    setError,
    setValue,
    reset,
  } = methods;

  // Leaving with dirty edits (onCancel, Escape) asks first. The guard steps
  // aside in two cases: inside a dialog or sheet, because a second modal on
  // top of an open one is forbidden (frontend.md, Destructive Actions); and
  // for a host that passes onDirtyChange with onCancel, because that host
  // gates its own onCancel through its own guard (see onDirtyChange).
  const formRef = React.useRef<HTMLFormElement>(null);
  const [inDialog, setInDialog] = React.useState(false);
  React.useLayoutEffect(() => {
    if (formRef.current?.closest('[role="dialog"], [role="alertdialog"]')) {
      setInDialog(true);
    }
  }, []);
  const hostGuardsExit = onCancel !== undefined && onDirtyChange !== undefined;
  const { confirmLeave, dialog } = useDirtyGuard(isDirty);
  const leave = React.useCallback(
    (next: () => void) => (inDialog ? next() : confirmLeave(next)),
    [inDialog, confirmLeave],
  );

  // #1684 D5: without an `onCancel`, Cancel and Escape discard the edits instead
  // of doing nothing. `reset()` without an argument restores react-hook-form's
  // own baseline (the values seeded at mount, or the post-save baseline set in
  // onFormSubmit), never the live `defaultValues` prop, which may still hold the
  // pre-save server values while the refetch after a save is in flight.
  // In flight (submit / delete / avatar upload) or pristine, nothing resets.
  const canReset = React.useCallback(() => {
    const fs = methods.formState;
    if (fs.isSubmitting || isSubmitting || isDeleting || isAvatarUploading) {
      return false;
    }
    return fs.isDirty;
  }, [methods, isSubmitting, isDeleting, isAvatarUploading]);
  const resetToDefaults = React.useCallback(() => {
    if (canReset()) reset();
  }, [canReset, reset]);

  // Without onCancel the Cancel button is the explicit "revert my edits"
  // control and resets at once. A host that guards its own exit gets the
  // untouched edits, since it may still answer "Keep editing". Otherwise a
  // discard resets before leaving, so a host that keeps the form mounted
  // shows the saved values.
  const cancelHandler = React.useCallback(() => {
    if (!onCancel) {
      resetToDefaults();
      return;
    }
    if (hostGuardsExit) {
      onCancel();
      return;
    }
    leave(() => {
      reset();
      onCancel();
    });
  }, [onCancel, hostGuardsExit, leave, reset, resetToDefaults]);

  // Escape is the accidental exit, so it asks in reset mode too. Returning
  // false declines the key press so the page handler gets it.
  const escapeReset = React.useCallback(() => {
    if (!canReset()) return false;
    leave(resetToDefaults);
    return true;
  }, [canReset, leave, resetToDefaults]);

  // Capture phase in both modes: a form mounted on a later tab registers after
  // the page's own useEscapeCancel(goBack), so a bubble-phase listener would
  // let the page leave unguarded. A pristine reset-mode form declines and the
  // page handles the key.
  useEscapeCancel(
    onCancel ? cancelHandler : hideDefaultActions ? undefined : escapeReset,
    { capture: true },
  );

  // Surface the dirty flag to the hosting page so it can arm the
  // unsaved-changes guard and its discard-confirmation dialog (issue #315).
  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const fieldMap = React.useMemo(() => {
    return fields.reduce(
      (acc, field) => {
        acc[field.name as string] = field;
        return acc;
      },
      {} as Record<string, FieldConfig<T>>,
    );
  }, [fields]);

  // Field names in on-screen order, for "first invalid field".
  const orderedFieldNames = React.useMemo(
    () =>
      groups.flatMap((group) =>
        group.fields.map(String).filter((name) => name in fieldMap),
      ),
    [groups, fieldMap],
  );

  // Scrolls by name and focuses the first focusable element of the field, so
  // custom controls (date pickers, selects, groups) that never register a ref
  // with react-hook-form still receive focus.
  const focusField = React.useCallback((name: string) => {
    const wrapper = formRef.current?.querySelector<HTMLElement>(
      `[data-field-name="${CSS.escape(name)}"]`,
    );
    if (!wrapper) return;
    wrapper.scrollIntoView?.({ block: "center" });
    const control = wrapper.querySelector<HTMLElement>(
      `#${CSS.escape(`field-${name}`)}`,
    );
    const target = control?.matches(FOCUSABLE)
      ? control
      : wrapper.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus({ preventScroll: true });
  }, []);

  // Fields are disabled while a submit is in flight and a disabled control
  // cannot take focus, so the request waits for the re-enabling render.
  const [pendingFocus, setPendingFocus] = React.useState<string | null>(null);
  const focusFirstError = React.useCallback(
    (keys: string[]) => {
      const first =
        orderedFieldNames.find((name) => keys.includes(name)) ??
        keys.find((key) => key !== "root");
      if (first) setPendingFocus(first);
    },
    [orderedFieldNames],
  );
  React.useEffect(() => {
    if (!pendingFocus || formStateIsSubmitting || isSubmitting) return;
    focusField(pendingFocus);
    setPendingFocus(null);
  }, [pendingFocus, formStateIsSubmitting, isSubmitting, focusField]);

  // Consumers with onCancel stay dirty after onSubmit resolves, because many
  // swallow their own errors and resolve anyway. The save is confirmed when
  // the server's values come back as new `defaultValues`; only then do the
  // current values become the clean baseline, so the guard stops asking.
  const defaultsKey = React.useMemo(
    () => defaultsKeyOf(defaultValues),
    [defaultValues],
  );
  const lastDefaultsKeyRef = React.useRef(defaultsKey);
  const awaitingSaveEchoRef = React.useRef(false);
  React.useEffect(() => {
    if (defaultsKey === lastDefaultsKeyRef.current) return;
    lastDefaultsKeyRef.current = defaultsKey;
    if (!awaitingSaveEchoRef.current || defaultsKey === null) return;
    awaitingSaveEchoRef.current = false;
    methods.reset(methods.getValues());
  }, [defaultsKey, methods]);

  const renderField = (fieldName: keyof T, field: FieldConfig<T>) => {
    const fieldId = `field-${String(fieldName)}`;
    const isRequired = field.required;
    const hasError = Boolean(
      (errors as Record<string, { message?: unknown }>)[fieldName as string],
    );

    return (
      <div
        key={String(fieldName)}
        className="space-y-2"
        data-field-name={String(fieldName)}
      >
        {/* `imageupload` renders its own FormLabel inside ImageUploadField, so a
            second label here would show the caption twice. */}
        {field.type !== "boolean" &&
          field.type !== "habitselection" &&
          field.type !== "imageupload" && (() => {
          const label = (
          <Label
            // Composite fields render a `role="group"` container, not a single
            // labelable control, so `htmlFor` would point at an element the HTML
            // label algorithm ignores — an inert association that reads as wired
            // but is not. Those fields name themselves via `aria-label`; here the
            // label is a plain visual caption. A plain Label: this sits outside
            // FormField/FormItem, so FormLabel would have no field to read.
            htmlFor={GROUP_FIELD_TYPES.has(field.type) ? undefined : fieldId}
            className="text-sm font-medium"
          >
            {field.label}
            {isRequired && <RequiredMarker />}
          </Label>
          );
          // The HelpTip sits beside the label, never inside it.
          return field.help ? (
            <div className="flex items-center gap-1">
              {label}
              <HelpTip content={field.help} label={field.helpLabel} />
            </div>
          ) : (
            label
          );
        })()}

        <Controller
          name={fieldName as FieldPath<T> & string}
          control={control}
          render={({
            field: controllerField,
          }: {
            field: ControllerRenderProps<T, FieldPath<T>>;
          }) => {
            // Avatar upload fields should only be disabled during actual submission,
            // not during form validation, to allow the Remove button to work
            const isDisabled =
              field.type === "avatarupload"
                ? field.disabled || isSubmitting
                : field.disabled || isSubmitting || formStateIsSubmitting;

            // Wire inline validation accessibility: errored inputs are marked
            // aria-invalid and point at their role="alert" error node; the
            // description (when present) is also linked via aria-describedby.
            const describedBy =
              [
                field.description && field.type !== "boolean"
                  ? `${fieldId}-description`
                  : null,
                hasError ? `${fieldId}-error` : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined;

            const commonProps = {
              id: fieldId,
              placeholder: field.placeholder,
              disabled: isDisabled,
              "aria-invalid": hasError ? true : undefined,
              "aria-describedby": describedBy,
            };

            // Use field registry to get the appropriate component
            const FieldComponent = getFieldComponent(field.type);

            // Add upload state tracking for avatar fields and make currentImageId reactive
            const additionalProps =
              field.type === "avatarupload"
                ? {
                    onUploadingChange: setIsAvatarUploading,
                    // Override the static currentImageId with the live form value
                    field: {
                      ...field,
                      avatarConfig: {
                        ...field.avatarConfig,
                        currentImageId:
                          (controllerField.value as unknown as string) ??
                          field.avatarConfig?.currentImageId,
                      },
                    },
                  }
                : {};

            // For avatar fields, pass the modified field with reactive currentImageId
            const fieldToPass =
              field.type === "avatarupload" && additionalProps.field
                ? additionalProps.field
                : field;

            const handleChange = (value: unknown) => {
              // An edit after the save must not be swallowed by its echo.
              awaitingSaveEchoRef.current = false;
              controllerField.onChange(value);
              if (onFieldChange) {
                onFieldChange(
                  String(fieldName),
                  value,
                  setValue as (
                    name: FieldPath<T>,
                    value: unknown,
                    options?: { shouldDirty?: boolean },
                  ) => void,
                );
              }
            };

            return (
              <FieldComponent
                field={fieldToPass}
                value={controllerField.value}
                onChange={handleChange}
                onBlur={controllerField.onBlur}
                {...commonProps}
                {...(field.type === "avatarupload"
                  ? { onUploadingChange: setIsAvatarUploading }
                  : {})}
              />
            );
          }}
        />

        {field.description && field.type !== "boolean" && (
          <p
            id={`${fieldId}-description`}
            className="text-sm text-muted-foreground"
          >
            {field.description}
          </p>
        )}

        {hasError && (
          <p
            id={`${fieldId}-error`}
            role="alert"
            className="text-sm text-destructive"
          >
            {String(
              (errors as Record<string, { message?: unknown }>)[
                fieldName as string
              ]?.message || "",
            )}
          </p>
        )}
      </div>
    );
  };

  const onFormSubmit = async (data: T) => {
    // `submitDisabled` greys the submit button, but that is presentation only: a
    // programmatic `form.requestSubmit()` — or implicit submission from Enter in a text
    // field — still runs the resolver and lands here, saving straight past the consumer's
    // domain guard (e.g. an allocation on an archived contract). This is the gate that
    // actually holds. Deliberately before the try: a blocked submit is not an error, so it
    // must not set the root error the catch below reports.
    if (submitDisabled) return;
    awaitingSaveEchoRef.current = false;
    const defaultsKeyAtSubmit = lastDefaultsKeyRef.current;
    try {
      // The context carries `dirtyFields` and `setValue` so a submit handler can
      // decide per field whether the user actually changed it (issue #1224,
      // decision D11) and can refresh one field after a rejection without
      // discarding the rest of the admin's edits.
      await onSubmit(data, {
        dirtyFields: dirtyFields as Partial<Record<keyof T, unknown>>,
        setValue: methods.setValue as FormSubmitContext<T>["setValue"],
        markSaved: (names) => {
          for (const name of names) {
            methods.resetField(name, { defaultValue: methods.getValues(name) });
          }
        },
      });
      // Reset fallback consumers (#1684 D5): the values just saved become the new
      // pristine baseline, so Cancel is disabled again and a later Cancel/Escape
      // never reverts to pre-save values. Consumers with onCancel wait for the
      // save's echo in `defaultValues` (see awaitingSaveEchoRef).
      if (onCancel === undefined) {
        methods.reset(methods.getValues());
      } else if (lastDefaultsKeyRef.current !== defaultsKeyAtSubmit) {
        // The refetch landed while onSubmit was still awaiting it.
        methods.reset(methods.getValues());
      } else {
        awaitingSaveEchoRef.current = true;
      }
    } catch (error: unknown) {
      // Fields the server rejected by name get their message inline; only an
      // error no rendered field explains falls back to the summary. Only the
      // fields of the current groups count (a stepped dialog renders one step),
      // and a nested key ("items[0].unit_price") marks its field: the inline
      // message and the summary read `errors[field].message` only.
      const renderedFields = Object.fromEntries(
        orderedFieldNames.map((name) => [name, true]),
      );
      const markedFields: string[] = [];
      let unmatched = false;
      const marked = applyServerFieldErrors(setError, error, (serverField) => {
        const [root] = serverFieldToFormPath(serverField).split(".");
        const name = resolveFieldName(root, renderedFields);
        if (!name) {
          unmatched = true;
          return undefined;
        }
        markedFields.push(name);
        return name;
      });
      if (marked) {
        focusFirstError(markedFields);
        // A rejected field no rendered field shows still needs the summary.
        if (!unmatched) return;
      }
      const unknownError = i18n._({
        id: "common.error.unknown",
        message: "An unknown error occurred",
      });
      setError("root" as FieldPath<T>, {
        type: "manual",
        // An ApiError's own message is the endpoint's Swagger status text
        // ("Validation errors or invalid request format"), so read the body.
        message:
          error && typeof error === "object" && "body" in error
            ? errorToastText(error, i18n, unknownError)
            : error instanceof Error
              ? error.message
              : unknownError,
      });
    }
  };

  const flatten = (err: unknown): unknown[] =>
    err &&
    typeof err === "object" &&
    !("message" in (err as Record<string, unknown>))
      ? Object.values(err as Record<string, unknown>).flatMap(flatten)
      : [err];
  const messageOf = (error: unknown) => {
    const m =
      typeof error === "object" && error && "message" in error
        ? (error as { message?: unknown }).message
        : undefined;
    return m
      ? String(m)
      : i18n._({
          id: "common.error.unknown",
          message: "An unknown error occurred",
        });
  };
  // Rendered fields are listed by label, in on-screen order, as links to the
  // field; their message is already inline. Anything else (root, a nested key
  // no field renders) keeps its message.
  const errorKeys = Object.keys(errors);
  const errorSummary: Array<string | FormErrorSummaryItem> = [
    ...orderedFieldNames
      .filter((name) => errorKeys.includes(name))
      .map((name) => ({
        key: name,
        label: fieldMap[name].label,
        onSelect: () => focusField(name),
      })),
    ...errorKeys
      .filter((key) => !(key in fieldMap))
      .flatMap((key) =>
        flatten((errors as Record<string, unknown>)[key])
          .filter(Boolean)
          .map(messageOf),
      ),
  ];
  const resolvedSubmitLabel = isAvatarUploading
    ? t({ message: "Uploading avatar..." })
    : (submitLabel ??
      (mode === "create"
        ? t({ message: "Create" })
        : i18n._({ id: "common.save", message: "Save" })));

  return (
    <FormProvider {...methods}>
      <form
        className={cn("flex flex-col", inSheet ? "h-full" : "min-h-full")}
        ref={formRef}
        onSubmit={handleSubmit(
          onFormSubmit as SubmitHandler<T>,
          (fieldErrors: FieldErrors<T>) =>
            focusFirstError(Object.keys(fieldErrors)),
        )}
        noValidate
      >
        {/* Responsive card grid. On a page it has no side inset, so the cards line up with the header and tabs. */}
        <div
          className={cn(
            "flex-1 overflow-y-auto w-full",
            inSheet ? "px-6 py-6" : "pb-6",
          )}
        >
          <div className={cn("grid gap-6 w-full", groupsClassName ?? "auto-fit-cards")}>
            {groups.map((group) => (
              // `data-group` is the stable DOM hook for a group card: tests and
              // e2e steps target it instead of a Tailwind class.
              <Card
                key={group.title}
                className={cn("w-full", group.muted && "opacity-60")}
                data-group={group.id}
                data-muted={group.muted ? "true" : undefined}
              >
                <CardHeader>
                  <CardTitle>{group.title}</CardTitle>
                  {group.description && (
                    <CardDescription>{group.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div
                    className={cn(
                      "grid gap-4",
                      group.fieldsClassName ??
                        (inSheet ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"),
                    )}
                  >
                    {group.fields.map((fieldName) => {
                      const field = fieldMap[String(fieldName)];
                      if (!field) return null;
                      return (
                        <div
                          key={String(fieldName)}
                          className={cn(
                            field.colSpan === 2 && !inSheet
                              ? "lg:col-span-2"
                              : "",
                          )}
                        >
                          {renderField(fieldName, field)}
                        </div>
                      );
                    })}
                  </div>
                  {group.id && groupSlots?.[group.id] && (
                    <div className="mt-4">{groupSlots[group.id]}</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional content (e.g., health entities) */}
          {children && <div className="mt-6">{children}</div>}
        </div>

        {/*
          UNMOUNTED, not `hidden` (hideDefaultActions -> no onCancel/submitLabel,
          so the bar renders no buttons). A `display:none` submit button is still
          the form's default button for implicit submission, so hiding it with a
          class left Enter-in-any-input silently triggering a full submit on
          consumers that render their own staged/step controls (they only render
          `type="button"`, so the hidden default was the first submit in tree
          order). Staged flows would jump straight to save, skipping later steps.
        */}
        <FormActionBar
          pinned={footerPinned}
          className={inSheet ? "px-6 py-4 flex-shrink-0" : undefined}
          errors={errorSummary}
          onDelete={
            !hideDefaultActions && mode === "edit" && onDelete
              ? onDelete
              : undefined
          }
          deleteLabel={deleteButtonLabel}
          deleteDisabled={isSubmitting || isDeleting || isAvatarUploading}
          isDeleting={isDeleting}
          onCancel={hideDefaultActions ? undefined : cancelHandler}
          cancelDisabled={
            isSubmitting ||
            isDeleting ||
            isAvatarUploading ||
            (onCancel === undefined && !isDirty)
          }
          submitLabel={hideDefaultActions ? undefined : resolvedSubmitLabel}
          // `onFormSubmit` awaits `onSubmit`, so formState.isSubmitting stays true
          // for the whole flight — gating on it blocks double-submit for every
          // consumer, including those that never pass the `isSubmitting` prop.
          submitDisabled={
            isSubmitting ||
            formStateIsSubmitting ||
            isDeleting ||
            isAvatarUploading ||
            submitDisabled
          }
          isSubmitting={
            !isAvatarUploading && (isSubmitting || formStateIsSubmitting)
          }
        />
      </form>
      {dialog}
    </FormProvider>
  );
}
